import { useEffect, useState } from 'react'
import * as statsApi from '../lib/api/stats'
import {
  publicCitiesProofCount,
  publicSocialProofCount,
  publicStampsProofCount,
} from '../lib/socialProofDisplay'

export type PublicSocialProofStatsState =
  | { status: 'idle' | 'loading' }
  | {
      status: 'success'
      users: number
      cities: number
      stamps: number
    }
  | { status: 'error' }

function settledValue<T>(result: PromiseSettledResult<T>): T | null {
  return result.status === 'fulfilled' ? result.value : null
}

export function usePublicSocialProofStats() {
  const [state, setState] = useState<PublicSocialProofStatsState>({ status: 'idle' })

  useEffect(() => {
    let cancelled = false
    setState({ status: 'loading' })

    void (async () => {
      const [usersRes, citiesRes, stampsRes] = await Promise.allSettled([
        statsApi.getRegisteredUserCount(),
        statsApi.getOriginCityCount(),
        statsApi.getCollectedStampsCount(),
      ])

      if (cancelled) return

      const usersRaw = settledValue(usersRes)
      const citiesRaw = settledValue(citiesRes)
      const stampsRaw = settledValue(stampsRes)

      if (usersRaw === null && citiesRaw === null && stampsRaw === null) {
        setState({ status: 'error' })
        return
      }

      setState({
        status: 'success',
        users: publicSocialProofCount(usersRaw ?? 0),
        cities: publicCitiesProofCount(citiesRaw ?? 0),
        stamps: publicStampsProofCount(stampsRaw ?? 0),
      })
    })()

    return () => {
      cancelled = true
    }
  }, [])

  return state
}
